import type { TFunction } from "i18next"
import type { ScheduledWorkflow } from "@/reducers/openclawWorkflowsSlice"
import { stopActionLabel } from "@/reducers/routes2gSlice"
import i18n from "@/lib/i18n/i18n"
import { getIntlLocale } from "@/lib/i18n/format"

type Schedule = NonNullable<ScheduledWorkflow["schedule"]>

/** Human-readable one-liner for a workflow's schedule, as the list shows it. */
export function formatSchedule(
  schedule: ScheduledWorkflow["schedule"],
  status: ScheduledWorkflow["status"],
  t: TFunction = i18n.t,
): string {
  if (!schedule) return ""

  if (schedule.kind === "at" && schedule.ts) {
    const d = new Date(schedule.ts)
    if (Number.isNaN(d.getTime())) return schedule.ts
    // Local-browser-tz formatted short date + time. Intentionally simple —
    // the dispatcher's locale handles weekday/month naming.
    const date = d.toLocaleDateString(getIntlLocale(), {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    const time = d.toLocaleTimeString(getIntlLocale(), {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    // For one-shots that have already fired (auto-paused by the cron service)
    // the date is historical context, so prefix with "fired" instead of "once
    // on" — surfaces *what already happened* rather than implying a future run.
    return status === "paused" && d.getTime() <= Date.now()
      ? t("ai.schedule.fired", { date, time })
      : t("ai.schedule.onceOn", { date, time })
  }

  if (schedule.kind === "every" && schedule.everyMs) {
    const mins = Math.round(schedule.everyMs / 60000)
    return mins < 60
      ? t("ai.schedule.everyMinutes", { value: mins })
      : t("ai.schedule.everyHours", { value: Math.round(mins / 60) })
  }

  if (schedule.kind === "event" && schedule.on === "order.completed") {
    return t("ai.schedule.orderCompleted")
  }

  if (
    schedule.kind === "event" &&
    schedule.on === "route.stop_dwell_exceeded"
  ) {
    const action = schedule.stopAction as string | undefined
    const where = action
      ? t("ai.schedule.dwell.whereEveryAction", {
          action,
          label: stopActionLabel(t, action) ?? action,
        })
      : t("ai.schedule.dwell.whereEveryStop")
    return schedule.minDwellMin
      ? t("ai.schedule.dwell.after", {
          duration: formatMinutes(schedule.minDwellMin),
          where,
        })
      : t("ai.schedule.dwell.longWait", { where })
  }

  if (schedule.kind === "event" && schedule.on === "document.analyzed") {
    const type = schedule.documentType
    if (schedule.onlyWithIssues) {
      return type != null
        ? t("ai.schedule.documentTypeWithIssues", { type })
        : t("ai.schedule.documentWithIssues")
    }
    return type != null
      ? t("ai.schedule.documentTypeSent", { type })
      : t("ai.schedule.documentSent")
  }

  if (
    schedule.kind === "event" &&
    (schedule.on === "vehicle.entered_zone" ||
      schedule.on === "vehicle.exited_zone")
  ) {
    return formatZoneEvent(schedule, t)
  }

  if (
    schedule.kind === "event" &&
    (schedule.on === "route.stop_arrived" || schedule.on === "route.stop_left")
  ) {
    return formatStopEvent(schedule, t)
  }

  if (schedule.kind === "cron" && schedule.expr) {
    return formatCron(schedule.expr, t)
  }

  return ""
}

function formatZoneEvent(schedule: Schedule, t: TFunction): string {
  const leaving = schedule.on === "vehicle.exited_zone"
  if (schedule.zone?.kind === "country") {
    const options = { country: schedule.zone.country }
    return leaving
      ? t("ai.schedule.exitZoneCountry", options)
      : t("ai.schedule.enterZoneCountry", options)
  }
  if (schedule.zone?.kind === "circle") {
    const options = {
      lat: schedule.zone.lat.toFixed(4),
      lon: schedule.zone.lng.toFixed(4),
      km: Math.round(schedule.zone.radius / 100) / 10,
    }
    return leaving
      ? t("ai.schedule.exitZoneCircle", options)
      : t("ai.schedule.enterZoneCircle", options)
  }
  return leaving ? t("ai.schedule.exitZone") : t("ai.schedule.enterZone")
}

function formatStopEvent(schedule: Schedule, t: TFunction): string {
  const leaving = schedule.on === "route.stop_left"
  // A pinned rule names no stop here — the route panel is where its identity
  // is legible; this list only has to say what kind of trigger it is.
  if (schedule.stopId) {
    return leaving
      ? t("ai.schedule.leaving.aStop")
      : t("ai.schedule.arriving.aStop")
  }
  const action = schedule.stopAction as string | undefined
  if (action === "pickup") {
    return leaving
      ? t("ai.schedule.leaving.everyPickup")
      : t("ai.schedule.arriving.everyPickup")
  }
  if (action === "delivery") {
    return leaving
      ? t("ai.schedule.leaving.everyDelivery")
      : t("ai.schedule.arriving.everyDelivery")
  }
  if (action) {
    const options = { action, label: stopActionLabel(t, action) ?? action }
    return leaving
      ? t("ai.schedule.leaving.everyAction", options)
      : t("ai.schedule.arriving.everyAction", options)
  }
  return leaving
    ? t("ai.schedule.leaving.everyStop")
    : t("ai.schedule.arriving.everyStop")
}

function formatCron(expr: string, t: TFunction): string {
  const parts = expr.trim().split(/\s+/)
  if (parts.length !== 5) return expr

  const [min, hour, dom, , dow] = parts
  // Only Monday to Friday gets a label; any other weekday field is not shown.
  const weekdays = dow === "1-5"
  const hours = hour.includes("-") ? hour.replace("-", "–") : null

  const intervalMatch = min.match(/^\*\/(\d+)$/) ?? min.match(/^0,(\d+)$/)
  if (intervalMatch) {
    const value = intervalMatch[1]
    if (hours && weekdays) {
      return t("ai.schedule.everyMinutesInHoursWeekdays", { value, hours })
    }
    if (hours) return t("ai.schedule.everyMinutesInHours", { value, hours })
    if (weekdays) return t("ai.schedule.everyMinutesWeekdays", { value })
    return t("ai.schedule.everyMinutes", { value })
  }

  if (!min.includes(",") && !min.includes("/") && !min.includes("-")) {
    const time = `${hour.padStart(2, "0")}:${min.padStart(2, "0")}`
    const domMatch = dom !== "*" ? dom.match(/^\*\/(\d+)$/) : null
    if (domMatch) {
      return t("ai.schedule.everyDaysAt", { value: domMatch[1], time })
    }
    if (weekdays) return t("ai.schedule.weekdaysAt", { time })
    return t("ai.schedule.dailyAt", { time })
  }

  return expr
}

/** "2 h", "1 h 30 min", "45 min". */
function formatMinutes(total: number): string {
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m} min`
  return m === 0 ? `${h} h` : `${h} h ${m} min`
}
